import {
  DrawerForm,
  DrawerSizeEnum,
  FontColorEnum,
  FontSizeEnum,
  MessageErrorIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ColumnContainer } from '@/components/FormFields/ColumnContainer';
import { HighlightContainer } from '@/components/FormFields/HighlightContainer';
import { InputEnrichments } from '@/components/FormFields/InputEnrichments';
import { InputFiles } from '@/components/FormFields/InputFiles';
import { InputSwitch } from '@/components/FormFields/InputSwitch';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { Headline } from '@/components/Headline/Headline';
import { useLanguageOptions } from '@/features/languages/hooks/useLanguageOptions';
import {
  useLocationCountyRegionBySubRegionCode,
  useLocationOptions,
} from '@/features/locations/hooks/useLocationOptions';
import { DrawerFormDataType, FormTypeEnum, State } from '@/types';

import { useChannels } from '../../api/get-channels';
import { CHANNEL_PROVIDERS_WITH_STREAM_URL } from '../../constants';
import { useAudienceChannelSourceOptions } from '../../hooks/useAudienceChannelSourceOptions';
import {
  DEFAULT_VALUES,
  useBroadcastersDrawerFormState,
} from '../../hooks/useBroadcastersDrawerFormState';
import { useChannelMediaTypeOptions } from '../../hooks/useChannelMediaTypeOptions';
import { useChannelOptions } from '../../hooks/useChannelOptions';
import { useChannelProviderOptions } from '../../hooks/useChannelProviderOptions';
import { useChannelScopeOptions } from '../../hooks/useChannelScopeOptions';
import { useChannelTypeOptions } from '../../hooks/useChannelTypeOptions';
import { useEconomicalValueChannelSourceOptions } from '../../hooks/useEconomicalValueChannelSourceOptions';
import { ChannelMediaTypeEnum, ChannelType, ChannelTypeEnum, TChannelForm } from '../../types';

export type TBroadcastersDrawerFormProps = {
  isOpen: boolean;
  data?: DrawerFormDataType<ChannelType> | null;
  onClose: () => void;
  onSubmit: (data: TChannelForm) => void;
  isMutating: boolean;
};

export const BroadcastersDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: TBroadcastersDrawerFormProps) => {
  const { t } = useTranslation();
  const { form } = useBroadcastersDrawerFormState();

  useEffect(() => {
    if (!isOpen) {
      form.reset(DEFAULT_VALUES);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!data?.data) return;

    const formData: TChannelForm = {
      ...data.data,
      short_name: data.data.short_name ?? '',
      av_code: data.data.av_code ?? null,
      parent_channel_id: data.data.parent_channel_id ?? '',
      language_id: data.data.language_id ?? '',
      logo: data.data.parameters?.logo_presigned_url,
    };

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }

    if (data.type === FormTypeEnum.DUPLICATE) {
      form.reset({
        ...formData,
        // the av code must be unique therefore require the user to type a new one
        av_code: null,
        name: `${data.data.name} (Copia)`,
      });
    }
  }, [data]);

  const channelOptions = useChannelOptions({
    isParentChannel: true,
  });
  const languageOptions = useLanguageOptions();
  const channelMediaTypeOptions = useChannelMediaTypeOptions();

  const parentChannelId = useWatch({
    control: form.control,
    name: 'parent_channel_id',
  });
  const { data: { data: channels = [] } = {} } = useChannels({
    params: {
      isParentChannel: true,
    },
  });
  const parentChannel = channels.find(channel => channel.id === parentChannelId);

  const syncFieldsFromParent = (parent?: ChannelType) => {
    form.setValue('language_id', parent?.language_id);
    form.setValue('logo', parent?.parameters?.logo_presigned_url);
    form.setValue('parameters.logo', parent?.parameters?.logo);
    form.setValue('scope', parent?.scope ?? null);
    form.setValue('country_code', parent?.country_code ?? '');
    form.setValue('region_code', parent?.region_code);
    form.setValue('subregion_code', parent?.subregion_code);
    if (parent?.media_type) {
      form.setValue('media_type', parent.media_type);
    }
  };

  const handleSubmit = form.handleSubmit(data => {
    onSubmit(data);
  });

  const hasErrors = form.formState.isSubmitted && Object.keys(form.formState.errors).length > 0;

  const countryCode = useWatch({
    control: form.control,
    name: 'country_code',
  });

  const regionCode = useWatch({
    control: form.control,
    name: 'region_code',
  });

  const { countryOptions, regionOptions, subregionOptions } = useLocationOptions({
    countryCode,
    regionCode,
  });

  const subregionCode = useWatch({
    control: form.control,
    name: 'subregion_code',
  });

  const { country, region } = useLocationCountyRegionBySubRegionCode(subregionCode || '');

  useEffect(() => {
    if (
      subregionCode &&
      country.value &&
      region.value &&
      subregionOptions.some(option => option.value === subregionCode)
    ) {
      form.setValue('country_code', country.value);
      form.setValue('region_code', region.value);
    }
  }, [subregionCode, country.value, region.value, form.setValue, subregionOptions]);

  useEffect(() => {
    const currentRegion = form.getValues('region_code');
    const currentSubregion = form.getValues('subregion_code');

    if (currentRegion && !regionOptions.some(option => option.value === currentRegion)) {
      form.setValue('region_code', '');
    }
    if (currentSubregion && !subregionOptions.some(option => option.value === currentSubregion)) {
      form.setValue('subregion_code', '');
    }
  }, [countryCode, regionOptions, subregionOptions, form]);

  useEffect(() => {
    const currentSubregion = form.getValues('subregion_code');

    if (currentSubregion && !subregionOptions.some(option => option.value === currentSubregion)) {
      form.setValue('subregion_code', '');
    }
  }, [regionCode, subregionOptions, form]);

  const channelType = useWatch({
    control: form.control,
    name: 'type',
  });

  const isBackup = channelType == ChannelTypeEnum.BACKUP;

  const childrenChannelOptions = useChannelOptions({
    isParentChannel: false,
  });

  const provider = useWatch({
    control: form.control,
    name: 'provider',
  });
  const hasStreamURL = provider ? CHANNEL_PROVIDERS_WITH_STREAM_URL.includes(provider) : false;

  const mediaType = useWatch({
    control: form.control,
    name: 'media_type',
  });
  const scope = useWatch({
    control: form.control,
    name: 'scope',
  });
  const [audienceName, setAudienceName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAudienceName('');
    }
  }, [isOpen]);

  const includeAudienceIds = useMemo(() => {
    const id = data?.data?.audience_channel_source_id ?? undefined;
    return id ? [id] : undefined;
  }, [data?.data?.audience_channel_source_id]);

  const audienceChannelSourceOptions = useAudienceChannelSourceOptions({
    mediaType,
    name: audienceName,
    includeIds: includeAudienceIds,
  });

  const economicalValueChannelSourceOptions = useEconomicalValueChannelSourceOptions({
    mediaType,
    scope,
    countryCode,
    regionCode: regionCode ?? undefined,
  });
  const channelScopeOptions = useChannelScopeOptions();
  const channelProviderOptions = useChannelProviderOptions();
  const channelTypeOptions = useChannelTypeOptions();
  const isChannelRadio = parentChannel?.media_type === ChannelMediaTypeEnum.RADIO;

  const prevFiltersRef = useRef<{ mediaType?: unknown; scope?: unknown; countryCode?: unknown }>(
    {}
  );
  useEffect(() => {
    const prev = prevFiltersRef.current;

    if (prev.mediaType !== undefined && prev.mediaType !== mediaType) {
      form.setValue('audience_channel_source_id', null);
    }

    if (
      prev.mediaType !== undefined &&
      (prev.mediaType !== mediaType || prev.scope !== scope || prev.countryCode !== countryCode)
    ) {
      form.setValue('economical_value_channel_source_id', null);
    }

    prevFiltersRef.current = { mediaType, scope, countryCode };
  }, [mediaType, scope, countryCode, form]);

  return (
    <DrawerForm
      title={
        !data?.data
          ? t('Crear nueva emisora')
          : data.type === FormTypeEnum.UPDATE
            ? t('Editar emisora')
            : t('Duplicar emisora')
      }
      isOpen={isOpen}
      onCancel={onClose}
      onSubmit={handleSubmit}
      size={DrawerSizeEnum.MD}
      isMutating={isMutating}
      labels={{
        submit: t('Guardar'),
        cancel: t('Cancelar'),
      }}
      components={{
        contextAction: hasErrors ? (
          <div className="flex items-center gap-size-4">
            <MessageErrorIcon className="text-error shrink-0" />
            <Typography color={FontColorEnum.Error} fontSize={FontSizeEnum.Sm}>
              {t('Hay campos obligatorios sin rellenar')}
            </Typography>
          </div>
        ) : undefined,
      }}
    >
      <InputSwitch
        form={form}
        name="state"
        states={{
          active: State.Active,
          inactive: State.Inactive,
        }}
      />
      <InputText
        form={form}
        name="name"
        required
        label={t('Nombre largo')}
        placeholder="Escribe aqui el nombre largo del medio"
      />
      <RowContainer>
        <InputText
          form={form}
          name="short_name"
          required
          label={t('Nombre corto')}
          placeholder="Escribe aqui el nombre corto"
        />
        <InputText
          form={form}
          name="av_code"
          label={t('ID AV (Legacy)')}
          placeholder={t('Escribe aqui el ID')}
        />
      </RowContainer>

      <RowContainer>
        <SelectForm
          form={form}
          required
          name="parent_channel_id"
          label={t('Emisora madre')}
          placeholder={t('Seleccionar...')}
          options={channelOptions}
          handleChange={value => {
            const id = (value as unknown as string) || null;
            const newParent = channels.find(channel => channel.id === id);
            syncFieldsFromParent(newParent);
          }}
        />

        <SelectForm
          form={form}
          required
          name="language_id"
          label={t('Idioma')}
          placeholder="Seleccionar..."
          options={languageOptions}
        />
      </RowContainer>

      <SelectForm
        form={form}
        name="media_type"
        required
        label={t('Soporte')}
        placeholder={t('Seleccione una emisora madre...')}
        options={channelMediaTypeOptions}
        disabled
      />

      <RowContainer>
        <InputFiles
          form={form}
          name="logo"
          label={t('Logo')}
          placeholder={t('Sube una imagen Recomendado 600px x 400px')}
          isImage
          accept="image/*"
          singleFile
        />

        <ColumnContainer>
          <SelectForm
            form={form}
            name="provider"
            label={t('Proveedor')}
            placeholder="Seleccionar..."
            options={channelProviderOptions}
          />

          <InputText
            form={form}
            name="uri"
            label={t('URL de stream')}
            placeholder={t('Escribe aquí la URL')}
            required={hasStreamURL}
            disabled={!hasStreamURL}
          />
        </ColumnContainer>
      </RowContainer>

      <SelectForm form={form} name="type" label={t('Tipo')} required options={channelTypeOptions} />

      {!isBackup ? (
        <HighlightContainer>
          <RowContainer>
            <SelectForm
              form={form}
              name="scope"
              required
              label={t('Ámbito')}
              placeholder={t('Seleccionar...')}
              options={channelScopeOptions}
            />

            <SelectForm
              form={form}
              name="country_code"
              required
              label={t('País')}
              placeholder={t('Seleccionar...')}
              options={countryOptions}
            />
          </RowContainer>

          <RowContainer>
            <SelectForm
              form={form}
              name="region_code"
              label={t('Autonomía')}
              placeholder={t('Seleccionar...')}
              options={regionOptions}
              noneOptionGender="feminine"
            />
            <SelectForm
              form={form}
              name="subregion_code"
              label={t('Provincia')}
              placeholder={t('Seleccionar...')}
              options={subregionOptions}
              noneOptionGender="feminine"
            />
          </RowContainer>

          <Headline text={t('Audiencias y tarifas')} />

          <RowContainer>
            <SelectForm
              form={form}
              name="audience_channel_source_id"
              label={t('ID audiencia')}
              placeholder={t('Seleccionar...')}
              options={audienceChannelSourceOptions}
              disabled={!mediaType}
              onFilterTextChange={setAudienceName}
              withFilter
            />

            <SelectForm
              form={form}
              name="economical_value_channel_source_id"
              label={t('ID Tarifas')}
              placeholder={t('Seleccionar...')}
              options={economicalValueChannelSourceOptions}
              disabled={!mediaType || !scope || !countryCode}
            />
          </RowContainer>
        </HighlightContainer>
      ) : (
        <HighlightContainer>
          <SelectForm
            form={form}
            name="backup_channel_id"
            label={t('Emisora de producción principal')}
            placeholder={t('Seleccionar...')}
            options={childrenChannelOptions}
            required
          />
        </HighlightContainer>
      )}

      <InputEnrichments form={form} isRadio={isChannelRadio} />
    </DrawerForm>
  );
};
